/**
 * The ArrayBufferBuilder class facilitates the construction
 * of a single aggregated ArrayBuffer from multiple sources.
 */
export class ArrayBufferBuilder {
  private readonly buffers: ArrayBuffer[] = [];

  /**
   * Adds a buffer to the internal storage.
   *
   * @param {ArrayBuffer | ArrayBufferView } buffer - The buffer to be added. It can be an ArrayBuffer, ArrayBufferView, or SJCLBitArray.
   * @return {this} - Returns the current instance for chaining.
   */
  public add(buffer: ArrayBuffer | ArrayBufferView): this {
    if (buffer instanceof ArrayBuffer) {
      this.buffers.push(buffer);
    } else if (ArrayBuffer.isView(buffer)) {
      this.buffers.push(new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength).slice().buffer);
    } else {
      throw new Error('Invalid buffer type');
    }
    return this;
  }

  /**
   * Combines multiple ArrayBuffer objects from the buffers property into a single Uint8Array.
   *
   * @return {Uint8Array} The combined Uint8Array representing the contents of all buffers.
   */
  public build(): Uint8Array<ArrayBuffer> {
    const totalLength = this.buffers.reduce((sum, buffer) => sum + buffer.byteLength, 0);
    const combinedBuffer = new Uint8Array(totalLength);

    let offset = 0;
    for (const buffer of this.buffers) {
      combinedBuffer.set(new Uint8Array(buffer), offset);
      offset += buffer.byteLength;
    }

    return combinedBuffer;
  }
}
